/**
 * The headcount as a counter — the alternate to the preset tiles.
 *
 * Tiles are quick when the number is one of four; they make any other number
 * a detour through "Custom". Here the number itself is the control: minus and
 * plus move it in fives, it can be typed into directly, and the presets sit
 * underneath as chips for the common jumps. Every path ends in the same
 * `onChange`, validated by the same rules, and priced by the same server
 * quote as the tiles.
 */

import { memo, useEffect, useState } from 'react';
import { Pressable, StyleSheet, TextInput, View, type ViewStyle } from 'react-native';

import { formatMoney, previewTotal, type AppConfig, type Money } from '@mfa/domain';
import { colors, fontWeight, radius, space } from '../../theme';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface QuantityStepperProps {
  presets: number[];
  unitPrice: Money | null;
  value: number | null;
  onChange: (count: number | null) => void;
  rules: AppConfig['people'];
  error?: string | null;
  testID?: string;
}

/** Donations come in batches; one at a time would be a lot of tapping. */
const STEP = 5;

const COUNTER_SIZE = 56;
const NUMBER_SIZE = 44;

/**
 * The browser's focus ring drew a blue outline over the green one on the
 * control a donor had just tapped. Selected state is already shown, so the
 * ring is dropped where it would only repeat it.
 */
const NO_OUTLINE = { outlineStyle: 'none' } as unknown as ViewStyle;

export function stepUp(value: number | null, rules: AppConfig['people'], presets: number[]): number {
  if (value === null) return Math.min(presets[0] ?? rules.min, rules.max);
  return Math.min(Math.floor(value / STEP) * STEP + STEP, rules.max);
}

export function stepDown(value: number, rules: AppConfig['people']): number {
  return Math.max(Math.ceil(value / STEP) * STEP - STEP, rules.min);
}

export const QuantityStepper = memo(function QuantityStepper({
  presets,
  unitPrice,
  value,
  onChange,
  rules,
  error,
  testID = 'quantity-stepper',
}: QuantityStepperProps) {
  const t = useT();
  const [text, setText] = useState(value === null ? '' : String(value));

  // The counter and the chips change the value from outside the input; the
  // field has to follow them rather than keep what was last typed.
  useEffect(() => {
    setText(value === null ? '' : String(value));
  }, [value]);

  const handleText = (next: string) => {
    const digits = next.replace(/[^0-9]/g, '');
    setText(digits);
    onChange(digits ? Number(digits) : null);
  };

  const canDecrease = value !== null && value > rules.min;
  const canIncrease = value === null || value < rules.max;
  // A live preview for the screen reader: the pinned total is the server's
  // figure, and this one is only ever the same multiplication.
  const preview = unitPrice && value ? formatMoney(previewTotal(unitPrice, value)) : null;

  return (
    <View testID={testID}>
      <View style={[styles.card, error ? styles.cardError : null]}>
        <View style={styles.counter}>
          <Pressable
            testID="stepper-decrease"
            onPress={() => value !== null && onChange(stepDown(value, rules))}
            disabled={!canDecrease}
            accessibilityRole="button"
            accessibilityLabel={t('Fewer people')}
            style={({ pressed }) => [
              styles.round,
              !canDecrease && styles.roundDisabled,
              pressed && styles.roundPressed,
              NO_OUTLINE,
            ]}
          >
            <Text variant="screenTitle" tone={canDecrease ? 'brand' : 'disabled'}>
              −
            </Text>
          </Pressable>

          <View style={styles.numberBlock}>
            <TextInput
              testID="stepper-input"
              value={text}
              onChangeText={handleText}
              keyboardType="number-pad"
              inputMode="numeric"
              placeholder="0"
              placeholderTextColor={colors.text.disabled}
              maxLength={String(rules.max).length}
              style={styles.number}
              accessibilityLabel={t('Number of people to feed')}
              accessibilityHint={preview ?? undefined}
              selectTextOnFocus
            />
            <Text variant="label" tone="secondary" align="center">
              {t('People')}
            </Text>
          </View>

          <Pressable
            testID="stepper-increase"
            onPress={() => onChange(stepUp(value, rules, presets))}
            disabled={!canIncrease}
            accessibilityRole="button"
            accessibilityLabel={t('More people')}
            style={({ pressed }) => [
              styles.round,
              !canIncrease && styles.roundDisabled,
              pressed && styles.roundPressed,
              NO_OUTLINE,
            ]}
          >
            <Text variant="screenTitle" tone={canIncrease ? 'brand' : 'disabled'}>
              +
            </Text>
          </Pressable>
        </View>

        {/* The presets live inside the card, under the number they set, so
            the whole control reads as one thing and fits a small phone. */}
        <View style={styles.chips} accessibilityRole="radiogroup">
          {presets.map((count) => {
            const selected = value === count;
            return (
              <Pressable
                key={count}
                testID={`stepper-preset-${count}`}
                onPress={() => onChange(count)}
                accessibilityRole="radio"
                accessibilityState={{ selected }}
                accessibilityLabel={`${count} ${t('People')}`}
                style={[styles.chip, selected && styles.chipSelected, NO_OUTLINE]}
              >
                <Text variant="bodyStrong" tone={selected ? 'onBrand' : 'primary'}>
                  {count}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      {error ? (
        <Text variant="caption" tone="danger" style={styles.error} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
    gap: space.md,
  },
  cardError: {
    borderColor: colors.text.danger,
  },
  counter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  round: {
    width: COUNTER_SIZE,
    height: COUNTER_SIZE,
    borderRadius: radius.full,
    borderWidth: 2,
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.brandSubtle,
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundDisabled: {
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surfaceSunken,
  },
  roundPressed: {
    opacity: 0.7,
  },
  numberBlock: {
    flex: 1,
    alignItems: 'center',
  },
  number: {
    width: '100%',
    textAlign: 'center',
    fontSize: NUMBER_SIZE,
    fontWeight: fontWeight.bold,
    color: colors.text.brand,
    paddingVertical: 0,
  },
  chips: {
    flexDirection: 'row',
    gap: space.sm,
  },
  chip: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: space.sm,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.bg.surface,
  },
  chipSelected: {
    backgroundColor: colors.bg.brand,
    borderColor: colors.bg.brand,
  },
  error: {
    marginTop: space.sm,
  },
});
